import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { publicCatalogApi } from "@/lib/api";

/**
 * Fetch and render pricing packages + options for a service page.
 * All values come from the /catalog/public endpoint (admin catalog is the single source of truth).
 *
 * Props:
 *   - category: string (e.g. "senior", "blindtest", "photobooth")
 *   - title, subtitle (rendered in the section header)
 *   - icons: dict mapping sort_order or index → lucide icon component (optional decorative)
 *   - testidPrefix: string used to build data-testid (e.g. "seniors-pack-")
 *   - comparisonExtraCols: array of { header, cell(pkg) } for the comparateur (optional)
 *   - fallback: static array to use if API fails / empty (keeps SSR/SEO safe)
 *   - featuredBadgeLabel: string (default "Le plus choisi")
 *   - ctaLabel: string (default "Demander ce forfait")
 *   - footerNote: string (below pricing)
 */
export function CatalogPackages({
  category,
  title,
  subtitle,
  icons = [],
  testidPrefix,
  comparisonExtraCols = [],
  featuredBadgeLabel = "Le plus choisi",
  ctaLabel = "Demander ce forfait",
  footerNote = "Tarifs TTC · Devis personnalisé disponible",
  showComparator = true,
  emptyLabel = "Nos forfaits arrivent bientôt.",
}) {
  const [packages, setPackages] = useState(null); // null = loading
  const [options, setOptions] = useState([]);

  useEffect(() => {
    publicCatalogApi.byCategory(category, true)
      .then((d) => { setPackages(d.packages || []); setOptions(d.options || []); })
      .catch(() => { setPackages([]); setOptions([]); });
  }, [category]);

  const fmtPrice = (p) => p.price_label && p.price_label.trim() ? p.price_label : `${Math.round(p.price_ttc)} €`;
  const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  /**
   * Split a price string like "À partir de 1 099 €" into { prefix: "À partir de", amount: "1 099 €" }.
   * If no known prefix is found, returns { prefix: "", amount: <full string> }.
   */
  const splitPrice = (p) => {
    const raw = (p.price_label && p.price_label.trim()) ? p.price_label.trim() : `${Math.round(p.price_ttc)} €`;
    const m = raw.match(/^(à partir de|dès|sur devis|à partir)\s+(.+)$/i);
    if (m) return { prefix: m[1], amount: m[2] };
    return { prefix: "", amount: raw };
  };

  // Resolve subtitle: allow (count) => ReactNode for dynamic counts
  const resolvedSubtitle = typeof subtitle === "function" ? subtitle(packages ? packages.length : 0) : subtitle;

  if (packages === null) {
    return (
      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-white/45 text-sm italic">Chargement des forfaits…</p>
        </div>
      </section>
    );
  }

  if (packages.length === 0) {
    return (
      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-white/45 text-sm italic">{emptyLabel}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15" data-testid={`${testidPrefix}forfaits-section`}>
        <div className="max-w-[1400px] mx-auto">
          {(title || subtitle) && (
            <div className="mb-14 reveal text-center">
              {title && <span className="eyebrow">{title}</span>}
              {resolvedSubtitle && (
                <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
                  {resolvedSubtitle}
                </h2>
              )}
            </div>
          )}

          <div className={`grid grid-cols-1 md:grid-cols-2 ${packages.length >= 4 ? "lg:grid-cols-4" : packages.length >= 3 ? "lg:grid-cols-3" : ""} gap-6`}>
            {packages.map((p, i) => {
              const Icon = icons[i] || icons[p.sort_order - 1] || null;
              const s = slug(p.name);
              return (
                <div
                  key={p.id}
                  data-testid={`${testidPrefix}${s}`}
                  className={`card-luxe p-7 md:p-8 reveal relative flex flex-col ${p.is_featured ? "border-[#DCB457]/60" : ""}`}
                >
                  {p.is_featured && (
                    <span className="absolute -top-3 left-6 bg-[#DCB457] text-black text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1">
                      {featuredBadgeLabel}
                    </span>
                  )}
                  {Icon && <Icon size={26} strokeWidth={1} className="text-[#DCB457] mb-4" />}
                  <h3 className="font-serif-luxe text-[22px] md:text-[26px] leading-[1.12] tracking-[-0.005em] pb-3 border-b border-[#DCB457]/15">{p.name}</h3>
                  {p.subtitle && <p className="text-white/55 text-[13px] mt-3 leading-relaxed min-h-[48px]">{p.subtitle}</p>}
                  <div className="mt-5 border-t border-[#DCB457]/20 pt-5">
                    {(() => { const sp = splitPrice(p); return (
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <div className="flex flex-col leading-none">
                          {sp.prefix && (
                            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5" data-testid={`${testidPrefix}${s}-price-prefix`}>{sp.prefix}</span>
                          )}
                          <span className="font-serif-luxe text-3xl md:text-4xl lg:text-4xl gold-text whitespace-nowrap" data-testid={`${testidPrefix}${s}-price`}>{sp.amount}</span>
                        </div>
                        {p.duration_hours != null && (
                          <span className="text-[11px] uppercase tracking-[0.18em] text-white/45">
                            {p.duration_hours >= 1 ? `${p.duration_hours} h` : `${p.duration_hours * 60} min`}
                          </span>
                        )}
                      </div>
                    ); })()}
                  </div>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-white/75 text-[13px] leading-snug">
                        <Check size={14} strokeWidth={1.5} className="text-[#DCB457] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`${testidPrefix}cta-${s}`}
                    className="btn-gold mt-7 w-full !py-3 !text-[11px]"
                  >
                    {ctaLabel}
                  </Link>
                </div>
              );
            })}
          </div>

          {options.length > 0 && (
            <div className="mt-14 reveal max-w-3xl mx-auto card-luxe p-7 md:p-8" data-testid={`${testidPrefix}options`}>
              <p className="eyebrow mb-4 text-center">Options possibles</p>
              <ul className="divide-y divide-[#DCB457]/15">
                {options.map((o) => (
                  <li key={o.id} className="py-3 flex items-center justify-between gap-4">
                    <span className="text-white/75 text-sm">{o.name}</span>
                    <span className="font-serif-luxe text-[#DCB457] text-lg whitespace-nowrap">{fmtPrice(o)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-center text-white/45 text-xs mt-8 uppercase tracking-[0.18em]">
            {footerNote}
          </p>
        </div>
      </section>

      {showComparator && packages.length >= 2 && (
        <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
          <div className="mb-10 reveal">
            <span className="eyebrow">Tableau comparatif</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.05]">
              Comparez nos <span className="italic gold-text">forfaits.</span>
            </h2>
          </div>
          <div className="card-luxe !p-0 overflow-x-auto reveal" data-testid={`${testidPrefix}compare-wrapper`}>
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-[#DCB457]/25">
                  <th className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5">Forfait</th>
                  <th className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5">Prix</th>
                  <th className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5">Durée</th>
                  {comparisonExtraCols.map((c, i) => (
                    <th key={i} className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5">{c.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {packages.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                    <td className="px-5 md:px-8 py-5 font-serif-luxe text-lg">{p.name}</td>
                    <td className="px-5 md:px-8 py-5 gold-text font-serif-luxe text-xl">{fmtPrice(p)}</td>
                    <td className="px-5 md:px-8 py-5 text-white/80 text-sm">
                      {p.duration_hours != null ? (p.duration_hours >= 1 ? `${p.duration_hours} h` : `${p.duration_hours * 60} min`) : "—"}
                    </td>
                    {comparisonExtraCols.map((c, i) => (
                      <td key={i} className="px-5 md:px-8 py-5">{c.cell(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
